import React, { useState, useEffect } from 'react'
import Modal from './Modal'
import { leadsApi, dealsApi, contactsApi } from '../services/api'
import { AlertTriangle, Trash2, Users, MessageSquare, Briefcase, Loader2 } from 'lucide-react'

interface BulkDeletePreview {
  leads?: number
  deals?: number
  contacts?: number
  conversations?: number
  messages?: number
  deal_activities?: number
}

interface BulkDeleteModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  type: 'leads' | 'deals' | 'contacts'
  selectedIds: number[]
}

const BulkDeleteModal: React.FC<BulkDeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  type,
  selectedIds,
}) => {
  const [preview, setPreview] = useState<BulkDeletePreview | null>(null)
  const [loading, setLoading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [includeDeals, setIncludeDeals] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isOpen && selectedIds.length > 0) {
      loadPreview()
    }
  }, [isOpen, selectedIds, includeDeals])

  const loadPreview = async () => {
    try {
      setLoading(true)
      setError('')
      let data: BulkDeletePreview
      if (type === 'leads') {
        data = await leadsApi.bulkDeletePreview(selectedIds, includeDeals)
      } else if (type === 'deals') {
        data = await dealsApi.bulkDeletePreview(selectedIds)
      } else {
        data = await contactsApi.bulkDeletePreview(selectedIds)
      }
      setPreview(data)
    } catch (err: any) {
      setError('Erro ao carregar preview da exclusão')
    } finally {
      setLoading(false)
    }
  }

  const handleConfirm = async () => {
    try {
      setDeleting(true)
      setError('')
      if (type === 'leads') {
        await leadsApi.bulkDelete(selectedIds, includeDeals)
      } else if (type === 'deals') {
        await dealsApi.bulkDelete(selectedIds)
      } else {
        await contactsApi.bulkDelete(selectedIds)
      }
      onConfirm()
    } catch (err: any) {
      setError('Erro ao excluir itens')
      setDeleting(false)
    }
  }

  const typeLabels = {
    leads: { singular: 'lead', plural: 'leads', icon: Users },
    deals: { singular: 'negócio', plural: 'negócios', icon: Briefcase },
    contacts: { singular: 'contato', plural: 'contatos', icon: MessageSquare },
  }

  const label = typeLabels[type]

  const totalRelated = preview
    ? (preview.contacts || 0) + (preview.conversations || 0) + (preview.messages || 0) + (preview.deals || 0) + (preview.deal_activities || 0)
    : 0

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Confirmar Exclusão em Massa" size="md">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        {/* Warning Icon */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: 'var(--radius-xl)',
            background: 'rgba(239, 68, 68, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <AlertTriangle size={24} color="#ef4444" />
          </div>
          <div>
            <p style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)' }}>
              Excluir {selectedIds.length} {selectedIds.length === 1 ? label.singular : label.plural}
            </p>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)', marginTop: '2px' }}>
              Esta ação não pode ser desfeita.
            </p>
          </div>
        </div>

        {/* Preview */}
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-6)', gap: 'var(--space-3)' }}>
            <Loader2 size={20} color="var(--color-primary-600)" style={{ animation: 'spin 1s linear infinite' }} />
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>Calculando itens vinculados...</span>
          </div>
        ) : preview && (
          <div style={{
            background: 'var(--glass-bg-subtle)',
            borderRadius: 'var(--radius-xl)',
            border: '1px solid var(--glass-border)',
            padding: 'var(--space-4)',
          }}>
            <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)', marginBottom: 'var(--space-3)' }}>
              Itens que serão excluídos:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {/* Main items */}
              {type === 'leads' && preview.leads !== undefined && (
                <PreviewRow label="Leads" count={preview.leads} color="#ef4444" />
              )}
              {type === 'deals' && preview.deals !== undefined && (
                <PreviewRow label="Negócios" count={preview.deals} color="#ef4444" />
              )}
              {type === 'contacts' && preview.contacts !== undefined && (
                <PreviewRow label="Contatos" count={preview.contacts} color="#ef4444" />
              )}

              {/* Related items */}
              {type === 'leads' && (preview.contacts || 0) > 0 && (
                <PreviewRow label="Contatos vinculados" count={preview.contacts!} color="#f59e0b" />
              )}
              {type === 'leads' && includeDeals && (preview.deals || 0) > 0 && (
                <PreviewRow label="Negócios vinculados" count={preview.deals!} color="#f59e0b" />
              )}
              {(preview.conversations || 0) > 0 && (
                <PreviewRow label="Conversas" count={preview.conversations!} color="#6b7280" />
              )}
              {(preview.messages || 0) > 0 && (
                <PreviewRow label="Mensagens" count={preview.messages!} color="#6b7280" />
              )}
              {(preview.deal_activities || 0) > 0 && (
                <PreviewRow label="Atividades de negócios" count={preview.deal_activities!} color="#6b7280" />
              )}
            </div>
          </div>
        )}

        {/* Include deals checkbox (leads only) */}
        {type === 'leads' && (
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-3) var(--space-4)',
            background: includeDeals ? 'rgba(239, 68, 68, 0.05)' : 'var(--glass-bg-subtle)',
            borderRadius: 'var(--radius-xl)',
            border: `1px solid ${includeDeals ? 'rgba(239, 68, 68, 0.2)' : 'var(--glass-border)'}`,
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}>
            <input
              type="checkbox"
              checked={includeDeals}
              onChange={(e) => setIncludeDeals(e.target.checked)}
              style={{ width: '18px', height: '18px', accentColor: '#ef4444', cursor: 'pointer' }}
            />
            <div>
              <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)' }}>
                Excluir negócios vinculados
              </p>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                Também exclui os deals associados a esses leads
              </p>
            </div>
          </label>
        )}

        {/* Error */}
        {error && (
          <div style={{
            padding: 'var(--space-3) var(--space-4)',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 'var(--radius-lg)',
          }}>
            <p style={{ fontSize: 'var(--text-sm)', color: '#ef4444' }}>{error}</p>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={deleting}
            style={{
              padding: 'var(--space-2-5) var(--space-5)',
              background: 'var(--glass-bg)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-xl)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--text-secondary)',
              cursor: deleting ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={deleting || loading}
            style={{
              padding: 'var(--space-2-5) var(--space-5)',
              background: deleting ? '#dc2626' : '#ef4444',
              border: 'none',
              borderRadius: 'var(--radius-xl)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'white',
              cursor: deleting || loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              opacity: deleting || loading ? 0.7 : 1,
              transition: 'all 0.2s',
            }}
          >
            {deleting ? (
              <>
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Excluindo...
              </>
            ) : (
              <>
                <Trash2 size={16} />
                Excluir permanentemente
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </Modal>
  )
}

const PreviewRow: React.FC<{ label: string; count: number; color: string }> = ({ label, count, color }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-2) 0' }}>
    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>{label}</span>
    <span style={{
      fontSize: 'var(--text-sm)',
      fontWeight: 'var(--font-weight-semibold)',
      color: color,
      background: `${color}15`,
      padding: '2px 10px',
      borderRadius: 'var(--radius-full)',
    }}>
      {count}
    </span>
  </div>
)

export default BulkDeleteModal
